"use client";

import { FullPageError } from "@carbon/ibm-products";
import { Button } from "@carbon/react";
import { usePathname, useRouter } from "next/navigation";

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  try {
    return String(error);
  } catch {
    return "Unknown error";
  }
}

function getErrorCode(error: unknown): string | undefined {
  if (error && typeof error === "object") {
    const anyErr = error as { code?: unknown; status?: unknown };
    if (typeof anyErr.code === "string") return anyErr.code;
    if (typeof anyErr.status === "string") return anyErr.status;
    if (typeof anyErr.status === "number") return String(anyErr.status);
  }
  return undefined;
}

function isUnauthorizedError(error: unknown): boolean {
  const msg = getErrorMessage(error);
  const code = getErrorCode(error);
  return (
    code === "401" ||
    msg.toLowerCase().includes("unauthorized") ||
    msg.includes("HTTP 401")
  );
}

type Props = {
  error: unknown;
  reset?: () => void;
};

export default function AppErrorFallback({ error, reset }: Props) {
  const path = usePathname();
  const router = useRouter();
  const unauthorized = isUnauthorizedError(error);

  const title = unauthorized ? "Session expired" : "Something went wrong";
  const label = unauthorized ? "Error 401" : "Error";
  const description = unauthorized
    ? "Your session is no longer valid. Please log in again to continue."
    : "Please try again. If the problem persists, contact your administrator.";

  const callbackUrl =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : path;
  const encodedCallback = encodeURIComponent(callbackUrl || "/home");

  const handleGoToLogin = () => {
    router.replace(`/login?callbackUrl=${encodedCallback}`);
    router.refresh();
  };

  const handleTryAgain = () => {
    // Next.js error boundaries provide reset() to retry rendering.
    // We also force a refresh to re-run server components/data fetching.
    reset?.();
    window.location.reload();
  };

  const showDebug =
    process.env.NODE_ENV !== "production" && !unauthorized;
  const debugMsg = showDebug ? getErrorMessage(error) : null;

  return (
    <FullPageError
      kind="custom"
      title={title}
      label={label}
      description={description}
    >
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Button kind={unauthorized ? "primary" : "tertiary"} onClick={handleGoToLogin}>
          Back to login
        </Button>
        <Button kind={unauthorized ? "tertiary" : "primary"} onClick={handleTryAgain}>
          Try again
        </Button>
      </div>
      {debugMsg ? (
        <pre
          style={{
            marginTop: 16,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            opacity: 0.8,
            maxWidth: 900,
          }}
        >
          {debugMsg}
        </pre>
      ) : null}
    </FullPageError>
  );
}
